import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { useSearch } from "../../hooks/useSearch";

const Header = ({ onMenuClick }) => {
  const { user } = useAuthContext();
  const { darkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('vi-VN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const isDashboard = location.pathname === '/dashboard';

  // ===== SEARCH =====
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { results, loading, search, getSuggestions } = useSearch(user);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery("");
        setSelectedIndex(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      if (searchQuery.trim()) {
        search(searchQuery);
        setSelectedIndex(-1);
      }
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, search]);

  const handleSelectResult = (path) => {
    setSearchOpen(false);
    setSearchQuery("");
    setSelectedIndex(-1);
    navigate(path);
  };

  const handleKeyDown = (e) => {
    const allItems = searchQuery.trim() ? results : getSuggestions();
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const item = allItems[selectedIndex];
      if (item) handleSelectResult(item.path);
    }
  };

  // ===== BREADCRUMB =====
  const getBreadcrumb = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    
    const map = {
      'dashboard': { label: 'Dashboard', icon: 'dashboard' },
      'diem-danh': { label: 'Điểm danh', icon: 'fact_check' },
      'nhap-diem': { label: 'Nhập điểm', icon: 'edit_note' },
      'quan-ly-hoc-sinh': { label: 'Quản lý HS', icon: 'group' },
      'quan-ly-nguoi-dung': { label: 'Quản lý người dùng', icon: 'manage_accounts' },
      'cai-dat': { label: 'Cài đặt', icon: 'settings' },
      'bao-cao': { label: 'Báo cáo', icon: 'monitoring' },
      'danh-gia-cuoi-ky': { label: 'Đánh giá cuối kỳ', icon: 'grading' },
      'hoc-sinh': { label: 'Hồ sơ học sinh', icon: 'person' },
      'doi-mat-khau': { label: 'Đổi mật khẩu', icon: 'lock' },
    };

    const items = segments.map((seg, index) => {
      const fullPath = '/' + segments.slice(0, index + 1).join('/');
      const info = map[seg] || { label: seg.replace(/-/g, ' '), icon: 'circle' };
      return { ...info, path: fullPath };
    });

    return items;
  };

  const breadcrumbs = getBreadcrumb();

  // ===== HÀM BACK - DÙNG window.history.back() =====
  const handleBack = () => {
    window.history.back();
  };

  return (
    <header className="h-16 sticky top-0 right-0 flex items-center px-4 lg:px-6 z-40 bg-surface border-b border-outline-variant gap-3">
      
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <button 
          onClick={() => {
            if (isDashboard) {
              onMenuClick();
            } else {
              handleBack();
            }
          }}
          className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-container transition-colors flex-shrink-0"
        >
          <span className="material-symbols-outlined text-on-surface-variant">
            {isDashboard ? 'menu' : 'arrow_back'}
          </span>
        </button>
                {/* Desktop Back button - chỉ hiện khi không ở Dashboard */}
        {!isDashboard && (
          <button 
            onClick={() => window.history.back()}
            className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center hover:bg-surface-container transition-colors flex-shrink-0"
            title="Quay lại"
          >
            <span className="material-symbols-outlined text-on-surface-variant">arrow_back</span>
          </button>
        )}
        <nav className="hidden sm:flex items-center gap-1 min-w-0">
          {breadcrumbs.map((item, index) => (
            <React.Fragment key={item.path}>
              {index > 0 && (
                <span className="material-symbols-outlined text-outline-variant text-[16px] flex-shrink-0">
                  chevron_right
                </span>
              )}
              <button
                onClick={() => index < breadcrumbs.length - 1 && navigate(item.path)}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg transition-colors truncate
                  ${index === breadcrumbs.length - 1
                    ? 'text-primary font-semibold cursor-default'
                    : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface cursor-pointer'
                  }`}
              >
                <span className="material-symbols-outlined text-[18px] flex-shrink-0">{item.icon}</span>
                <span className="text-body-md truncate">{item.label}</span>
              </button>
            </React.Fragment>
          ))}
        </nav>

        <div className="sm:hidden flex flex-col min-w-0">
          <h2 className="text-section-title font-section-title text-primary truncate">
            {breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : 'Dashboard'}
          </h2>
          <span className="text-label-caps text-on-surface-variant/60 truncate">{today}</span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button 
          onClick={toggleDarkMode}
          className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center hover:bg-surface-container transition-colors"
          title={darkMode ? 'Chế độ sáng' : 'Chế độ tối'}
        >
          <span className="material-symbols-outlined text-on-surface-variant">
            {darkMode ? 'light_mode' : 'dark_mode'}
          </span>
        </button>

        <div className="relative">
          {!searchOpen && (
            <button
              onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 100); }}
              className="flex items-center gap-2 h-9 px-3 bg-surface-container-low hover:bg-surface-container rounded-full border border-outline-variant text-on-surface-variant text-body-md transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">search</span>
              <span className="hidden md:inline text-on-surface-variant/60">Tìm kiếm...</span>
              <kbd className="hidden lg:inline-flex items-center px-1.5 py-0.5 bg-surface border border-outline-variant rounded text-[10px] font-mono text-on-surface-variant/50">Ctrl+K</kbd>
            </button>
          )}

          {searchOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => { setSearchOpen(false); setSearchQuery(""); }} />
              <div className="fixed sm:absolute sm:right-0 top-0 sm:top-0 left-0 sm:left-auto right-0 sm:w-[400px] w-full h-full sm:h-auto sm:max-h-[500px] bg-surface-container-lowest sm:rounded-2xl shadow-lg border-0 sm:border border-outline-variant overflow-hidden z-50 flex flex-col" onKeyDown={handleKeyDown}>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-outline-variant flex-shrink-0">
                  <button onClick={() => { setSearchOpen(false); setSearchQuery(""); }} className="sm:hidden w-8 h-8 rounded-full flex items-center justify-center hover:bg-surface-container transition-colors flex-shrink-0"><span className="material-symbols-outlined text-on-surface-variant">arrow_back</span></button>
                  <span className="material-symbols-outlined text-on-surface-variant hidden sm:block">search</span>
                  <input ref={searchRef} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Tìm học sinh, người dùng, lớp..." className="flex-1 bg-transparent border-none outline-none text-body-md text-on-surface placeholder:text-on-surface-variant/50" autoFocus />
                  {loading && <span className="material-symbols-outlined text-on-surface-variant animate-spin text-[18px]">sync</span>}
                  {searchQuery && <button onClick={() => { setSearchQuery(""); searchRef.current?.focus(); }} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-surface-container transition-colors"><span className="material-symbols-outlined text-[16px] text-on-surface-variant">close</span></button>}
                </div>
                <div className="flex-1 overflow-y-auto py-2">
                  {searchQuery.trim() === '' ? (
                    <>
                      <p className="px-4 py-2 text-label-caps text-on-surface-variant/60 uppercase">Tìm nhanh</p>
                      {getSuggestions().map((item, index) => (
                        <button key={item.id} onClick={() => handleSelectResult(item.path)} className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-container transition-colors text-left ${selectedIndex === index ? 'bg-surface-container' : ''}`}>
                          <span className="material-symbols-outlined text-on-surface-variant">{item.icon}</span>
                          <div className="flex-1 min-w-0"><span className="text-body-md text-on-surface">{item.label}</span><p className="text-label-caps text-on-surface-variant/50">{item.subtitle}</p></div>
                          <span className="material-symbols-outlined text-outline-variant text-[16px]">arrow_forward</span>
                        </button>
                      ))}
                    </>
                  ) : results.length > 0 ? (
                    <>
                      <p className="px-4 py-2 text-label-caps text-on-surface-variant/60 uppercase">{results.length} kết quả</p>
                      {results.map((item, index) => (
                        <button key={`${item.type}-${item.id || index}`} onClick={() => handleSelectResult(item.path)} className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-container transition-colors text-left border-b border-outline-variant/50 last:border-0 ${selectedIndex === index ? 'bg-surface-container' : ''}`}>
                          <div className={`w-9 h-9 rounded-lg ${item.bg} flex items-center justify-center flex-shrink-0`}><span className={`material-symbols-outlined ${item.color} text-[20px]`}>{item.icon}</span></div>
                          <div className="flex-1 min-w-0"><p className="text-body-md font-medium text-on-surface truncate">{item.label}</p><p className="text-label-caps text-on-surface-variant truncate">{item.subtitle}</p></div>
                          <span className="material-symbols-outlined text-outline-variant text-[16px]">arrow_forward</span>
                        </button>
                      ))}
                    </>
                  ) : (
                    <div className="px-4 py-8 text-center"><span className="material-symbols-outlined text-[40px] text-outline-variant">search_off</span><p className="text-body-md text-on-surface-variant mt-2">Không tìm thấy kết quả</p><p className="text-label-caps text-on-surface-variant/50 mt-1">Thử tìm với từ khóa khác</p></div>
                  )}
                </div>
                <div className="hidden sm:flex items-center gap-4 px-4 py-2 border-t border-outline-variant text-[10px] text-on-surface-variant/50 flex-shrink-0">
                  <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-surface-container rounded text-[10px]">↑↓</kbd> Di chuyển</span>
                  <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-surface-container rounded text-[10px]">Enter</kbd> Chọn</span>
                  <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-surface-container rounded text-[10px]">Esc</kbd> Đóng</span>
                </div>
              </div>
            </>
          )}
        </div>

        <button className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center hover:bg-surface-container transition-colors relative">
          <span className="material-symbols-outlined text-on-surface-variant">notifications</span>
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-error rounded-full border-2 border-surface"></span>
        </button>

        <div className="w-9 h-9 rounded-full bg-primary-fixed flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
          {user?.hoTen?.charAt(0) || "U"}
        </div>
      </div>
    </header>
  );
};

export default Header;